import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { MenuItem, ListItemIcon, ListItemText, Box } from '@mui/material';

import { useHoverMenu } from './use-hover-menu';
import {
  HoverMenu,
  HoverSubmenuContext,
  useHoverSubmenuContext,
} from './hover-menu';

import type { HoverMenuItemProps } from './types';

// ----------------------------------------------------------------------

/**
 * A MenuItem that supports hover-based submenus.
 *
 * When `submenu` prop is provided, the item will display a chevron icon
 * and open the submenu when hovered. The submenu will stay open while
 * the cursor is over either the menu item or the submenu itself.
 *
 * @example
 * ```tsx
 * // Simple menu item with icon on left (legacy)
 * <HoverMenuItem icon={<SettingsIcon />}>
 *   Settings
 * </HoverMenuItem>
 *
 * // Custom layout with icon on right
 * <HoverMenuItem submenu={...}>
 *   <Typography sx={{ flex: 1 }}>Language</Typography>
 *   <LanguageIcon />
 * </HoverMenuItem>
 * ```
 */
export function HoverMenuItem({
  children,
  icon,
  submenu,
  submenuProps,
  closeDelay = 200,
  submenuPaperSx,
  onClick,
  hideChevron = false,
  submenuPosition = 'right',
  ...other
}: HoverMenuItemProps) {
  const { open, anchorEl, elementRef, onOpen, onClose, closeImmediately } =
    useHoverMenu<HTMLLIElement>({ closeDelay });

  const parentContext = useHoverSubmenuContext();

  const hasSubmenu = Boolean(submenu);

  const handleClick = (event: React.MouseEvent<HTMLLIElement>) => {
    if (hasSubmenu) {
      // If has submenu, toggle it on click (for accessibility/touch)
      if (open) {
        closeImmediately();
      } else {
        onOpen();
      }
    } else {
      // No submenu - execute the click handler and close all menus
      onClick?.(event);
      parentContext.closeAll();
    }
  };

  const handleCloseAll = () => {
    closeImmediately();
    parentContext.closeAll();
  };

  // Determine if children is a simple string/text or custom layout
  const isSimpleChildren = typeof children === 'string' || !icon;

  return (
    <>
      <MenuItem
        ref={elementRef}
        onClick={handleClick}
        onMouseEnter={hasSubmenu ? onOpen : undefined}
        onMouseLeave={hasSubmenu ? onClose : undefined}
        selected={open}
        {...other}
      >
        {icon && <ListItemIcon>{icon}</ListItemIcon>}
        {isSimpleChildren && !icon ? (
          // Custom children layout (e.g., flex row with icon on right)
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            {children}
          </Box>
        ) : (
          // Legacy layout with ListItemText
          <ListItemText>{children}</ListItemText>
        )}
        {hasSubmenu && !hideChevron && (
          <ChevronRightIcon fontSize="small" sx={{ ml: 1 }} />
        )}
      </MenuItem>

      {hasSubmenu && (
        <HoverSubmenuContext.Provider value={{ closeAll: handleCloseAll }}>
          <HoverMenu
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            onMouseEnter={onOpen}
            onMouseLeave={onClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: submenuPosition === 'left' ? 'left' : 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: submenuPosition === 'left' ? 'right' : 'left',
            }}
            {...submenuProps}
            slotProps={{
              ...submenuProps?.slotProps,
              paper: {
                ...submenuProps?.slotProps?.paper,
                sx: submenuPaperSx,
              },
            }}
          >
            {submenu}
          </HoverMenu>
        </HoverSubmenuContext.Provider>
      )}
    </>
  );
}
